import { ulBrush01 } from '@faceup/icons/ulBrush01'
import { ulFile02 } from '@faceup/icons/ulFile02'
import { ulFileShield02 } from '@faceup/icons/ulFileShield02'
import { ulFolder } from '@faceup/icons/ulFolder'
import { ulFolderPlus } from '@faceup/icons/ulFolderPlus'
import { ulInbox01 } from '@faceup/icons/ulInbox01'
import { ulPhoneCall01 } from '@faceup/icons/ulPhoneCall01'
import { ulPhoneOutgoing02 } from '@faceup/icons/ulPhoneOutgoing02'
import { ulPieChart01 } from '@faceup/icons/ulPieChart01'
import { ulSearchRefraction } from '@faceup/icons/ulSearchRefraction'
import { ulServer05 } from '@faceup/icons/ulServer05'
import { ulSettings01 } from '@faceup/icons/ulSettings01'
import { ulUsers02 } from '@faceup/icons/ulUsers02'
import { ulZap } from '@faceup/icons/ulZap'
import { defineMessages, useIntl, useLocalization } from '@faceup/localization'
import { ucAiPhone, ucLinking, ucMessageSettings } from '@faceup/ui'
import {
  canAddMoreMembers,
  canAddMoreReportingForms,
  Feature,
  Institution,
  Language,
  Plan,
} from '@faceup/utils'
import { ucClipboard } from '../../../customIcons/ucClipboard'
import { useAccessRights, usePartner } from '../../../hooks'
import { useInstitution } from '../../../providers'
import type { UpsellData } from '../upsellData'
import imageEmployeeManagementCs from './assets/upsell-employee-management-cs.png'
import imageEmployeeManagementEn from './assets/upsell-employee-management-en.png'
import imageInvestigationsCs from './assets/upsell-investigations-cs.png'
import imageInvestigationsEn from './assets/upsell-investigations-en.png'
import imageOrganizationStructureCs from './assets/upsell-organization-structure-cs.png'
import imageOrganizationStructureEn from './assets/upsell-organization-structure-en.png'
import imageReportsCs from './assets/upsell-reports-cs.png'
import imageStatisticsCs from './assets/upsell-statistics-cs.png'
import imageStatisticsEn from './assets/upsell-statistics-en.png'
import imageSurveysCs from './assets/upsell-surveys-cs.png'
import imageSurveysEn from './assets/upsell-surveys-en.png'
import imageWhitelabelCs from './assets/upsell-whitelabel-cs.png'
import imageWhitelabelEn from './assets/upsell-whitelabel-en.png'

const upsellForValues = [
  'reports',
  'statistics',
  'organisationStructure',
  'categories',
  'caseStatuses',
  'closingReasons',
  'formAppearance',
  'addChannelLiveHotline',
  'addChannelAutomatedHotline',
  'addChannelAiHotline',
  'addChannelForm',
  'addMember',
  'automaticDueDate',
  'endToEndEncryption',
  'schoolEnableAccessKey',
  'materials',
  'sso',
  'webhooks',
  'apiKeys',
  'whitelabel',
  'schoolEditUserPermissions',
  'integrations',
  'surveys',
  'investigations',
  'ipAllowlist',
  'employeeManagement',
  'disclosures',
  'customDocuments',
  'caseManagement',
] as const

const messages = defineMessages('institution.upsell', upsellForValues, {
  reports: {
    title: 'Get access to cases and communicate with the sender',
    features: 'Case management\nFollow-up communication\nInternal comments\nActivity log',
    description:
      'You can view received cases in a clear overview in the administration. You can communicate with the sender and label cases with tags, priority or due date.',
  },
  statistics: {
    title: 'Check statistics of received cases',
    features:
      'Visualized and actionable data\nAdvanced filtering\nAverage case resolution time, open time, feedback and more',
    description:
      'In the Analytics section, you can view statistics on received cases. All data can be filtered and exported by time, categories, tags, and organizational units.',
  },
  organisationStructure: {
    title: 'Create an organizational structure',
    features:
      'Unlimited organizational units\nCase submission to the concrete units\nAccess settings by units',
    description:
      'Do you have multiple departments or branches? Create an organizational structure according to your needs and set member access to each organizational unit.',
  },
  categories: {
    title: 'Manage categories',
    features: '',
    description:
      'Add your own case categories and adjust their order. With the higher version of FaceUp, you can customize case categories according to your own needs.',
  },
  caseStatuses: {
    title: 'Custom case statuses',
    features: '',
    description:
      'Manage case statuses feature allows creating, editing, and organizing case statuses. You can define your own statuses to match your internal processes.',
  },
  closingReasons: {
    title: 'Custom case closing reasons',
    features: '',
    description:
      'Manage case closing reasons feature allows creating, editing, and organizing case closing reasons. You can define your own reasons to match your internal processes.',
  },
  formAppearance: {
    title: 'Customize the link to the form',
    features: '',
    description: 'Create your own unique link to the form.',
  },
  addChannelLiveHotline: {
    title: 'Get a live hotline',
    features: 'Emotional support\nEase-of-use\nReal person\nRegulatory-compliant scripts',
    description: 'Live hotline provides agents who talk to the calling person in real time.',
  },
  addChannelAiHotline: {
    title: 'Get an AI-powered hotline',
    features: 'Activate the hotline',
    description:
      'Enable whistleblowers to confide in you via a hotline. You will see these cases in the same overview as cases received through the form.',
  },
  addChannelAutomatedHotline: {
    title: 'Activate the hotline',
    features: '',
    description:
      'Enable whistleblowers to confide in you via a hotline. You will see these cases in the same overview as cases received through the form.',
  },
  addChannelForm: {
    title: 'Add more forms',
    features: '',
    description:
      'Create additional forms for different target groups. In addition to the whistleblower form, you can have additional forms for internal use, or create online questionnaires, etc.',
  },
  addMember: {
    title: 'Add more members',
    features: '',
    description:
      'Add your colleagues or external partners to the FaceUp administration. You can set different types of access for these members - to cases, categories, etc.',
  },
  automaticDueDate: {
    title: 'Set up automatic due dates',
    features: '',
    description:
      'Ensure you don’t forget to respond to cases within a due date. Once the response due date approaches, you will receive a notification by email.',
  },
  endToEndEncryption: {
    title: 'Turn on E2EE encryption',
    features: '',
    description:
      'The highest level of E2EE security ensures that all your data and cases remain secure under all circumstances.',
  },
  schoolEnableAccessKey: {
    title: 'Set up an access code',
    features: '',
    description:
      'If you want only your students and their parents to have access to the form, set up a unique access code that you share with them.',
  },
  materials: {
    title: 'Have all materials available',
    features: '',
    description:
      'Together with the experts, we have prepared a range of useful materials for you to use in the prevention and handling of cases, or to promote the online trust box.',
  },
  sso: {
    title: 'Sign in with a company account',
    features: '',
    description:
      'Enable single sign-on (SSO) and members will be able to sign in to the FaceUp administration using their company email.',
  },
  webhooks: {
    title: 'Webhooks',
    features: '',
    description:
      'With this feature, you can set up multiple webhooks that will be notified whenever an event occurs (e.g. a new case).',
  },
  apiKeys: {
    title: 'API',
    features: '',
    description:
      'The API allows you to automate some operations using services like Make and Zapier.',
  },
  whitelabel: {
    title: 'Custom branding',
    features: 'Custom logo\nCustom colors\nFaceUp branding settings',
    description:
      'Here you can change the FaceUp logo to your custom one, set the administration color scheme and the default form color scheme.',
  },
  schoolEditUserPermissions: {
    title: 'Set access and permissions for members',
    features: '',
    description:
      'You can set up and edit different types of access to cases (by category and organizational units) and settings for individual members.',
  },
  integrations: {
    title: 'Get integrations',
    features: '',
    description: 'Connect FaceUp to other services you already use.',
  },
  surveys: {
    title: 'Get access to the surveys',
    features:
      'Creating surveys\nCollecting responses\nData analysis and graphic display of results',
    description:
      'Do you want to better understand the needs and expectations of your employees, clients or business partners? Create a survey and collect anonymous responses.',
  },
  investigations: {
    title: 'Conduct investigations',
    features: 'Record accurate data\nIdentify the investigator\nUse the HR integrations',
    description:
      'For each case, you can create an investigation and record all the important steps, findings, developments and conclusions.',
  },
  ipAllowlist: {
    title: 'IP address restriction',
    features:
      'Increases account security. \nUnlimited number of IP addresses allowed.\nIP address allowlist can be changed anytime.',
    description:
      "Allows you to restrict login access to your organization's account only from specified IP addresses.",
  },
  employeeManagement: {
    title: 'Unlock employee management & insights',
    features:
      'Seamless integration with HR systems (e.g., BambooHR, UKG Ready)\nEasy CSV import or manual entry of employee records\nView employee details within case and investigation records',
    description:
      'Take control of your employee data and elevate your case management. With access to the Employees section, you can manage your team, send surveys, and gain deeper insights - all from one place.',
  },
  disclosures: {
    title: 'Collect disclosures from your people',
    features:
      'Custom disclosure forms\nSharing by a public link\nReview and approval of every submission',
    description:
      'Need a declaration of conflicts of interest or gifts received? Create a disclosure form, share it with the people who have to file it, and review all submissions in one place.',
  },
  customDocuments: {
    title: 'Keep your documents in FaceUp',
    features:
      'Upload of your own files\nOrganizing documents into folders\nAccess for your members',
    description:
      'Store internal policies, guidelines and other documents directly in FaceUp, so your members always find the current version in the same place they handle cases.',
  },
  caseManagement: {
    title: 'Receive and manage cases',
    features:
      'Reporting channels for your organization\nFollow-up communication with the sender\nAttachments and case translations',
    description:
      'Set up the channels people use to reach you and handle every received case directly in the FaceUp administration.',
  },
})

type UpsellFor =
  | 'reports'
  | 'statistics'
  | 'organisationStructure'
  | 'categories'
  | 'caseStatuses'
  | 'closingReasons'
  | 'addChannelLiveHotline'
  | 'addChannelAutomatedHotline'
  | 'addChannelAiHotline'
  | 'addChannelForm'
  | 'addMember'
  | 'automaticDueDate'
  | 'formAppearance'
  | 'endToEndEncryption'
  | 'schoolEnableAccessKey'
  | 'schoolEditUserPermissions'
  | 'sso'
  | 'materials'
  | 'whitelabel'
  | 'surveys'
  | 'webhooks'
  | 'apiKeys'
  | 'integrations'
  | 'investigations'
  | 'ipAllowlist'
  | 'employeeManagement'
  | 'disclosures'
  | 'customDocuments'
  | 'caseManagement'

type Material = {
  id: string
  isAccessible: boolean
  downloadLink: string | null
}

export type UpsellReturnType = Record<Exclude<UpsellFor, 'materials'>, UpsellData> & {
  materials: (material: Material) => UpsellData
}

export const useUpsell = (): UpsellReturnType => {
  const { formatMessage } = useIntl()
  const {
    data: { plan, institution, membersCount, formsCount, entitlements, hasEmployees },
  } = useInstitution({
    // Should be better separated
    __unsafeSkipCheckIfNotWrappedInInstitutionProvider: true,
  })
  const {
    isAllowedE2EE,
    isAllowedSSO,
    isAllowedWebhooks,
    isAllowedWhitelabel,
    isAllowedApi,
    isAllowedMultipleReportSources,
  } = useAccessRights()
  const { isPartnerAdministration } = usePartner()
  const { language } = useLocalization()

  const isEntitlementEnabled = (feature: Feature) =>
    entitlements.find(entitlement => entitlement.id === feature)?.isEnabled ?? false

  const membersEntitlement = entitlements?.find(
    entitlement => entitlement.id === Feature.TeamMembers
  )
  const canAddMoreUsers =
    membersEntitlement?.isEnabled && canAddMoreMembers(membersEntitlement?.value, membersCount)

  const reportingFormsEntitlement = entitlements?.find(
    entitlement => entitlement.id === Feature.ReportingForms
  )
  const canAddMoreForms =
    reportingFormsEntitlement?.isEnabled &&
    canAddMoreReportingForms(reportingFormsEntitlement?.value, formsCount)

  const showCzechImage = [Language.Cs, Language.Sk].includes(language)

  return {
    reports: {
      name: 'reports',
      icon: ulInbox01,
      title: formatMessage(messages.reports.title),
      features: formatMessage(messages.reports.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.reports.description),
      image: imageReportsCs,
      showUpsell: !isPartnerAdministration && !isEntitlementEnabled(Feature.CaseManagement),
    },
    statistics: {
      name: 'statistics',
      icon: ulPieChart01,
      title: formatMessage(messages.statistics.title),
      features: formatMessage(messages.statistics.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.statistics.description),
      image: showCzechImage ? imageStatisticsCs : imageStatisticsEn,
      showUpsell: !isPartnerAdministration && !isEntitlementEnabled(Feature.CaseManagement),
    },
    organisationStructure: {
      name: 'organisationStructure',
      icon: ulSettings01,
      title: formatMessage(messages.organisationStructure.title),
      features: formatMessage(messages.organisationStructure.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.organisationStructure.description),
      image: showCzechImage ? imageOrganizationStructureCs : imageOrganizationStructureEn,
      showUpsell: plan === Plan.Free,
    },
    categories: {
      name: 'categories',
      icon: ucMessageSettings,
      title: formatMessage(messages.categories.title),
      features: formatMessage(messages.categories.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.categories.description),
      showUpsell: !isEntitlementEnabled(Feature.CaseManagement),
    },
    caseStatuses: {
      name: 'caseStatuses',
      icon: ucMessageSettings,
      title: formatMessage(messages.caseStatuses.title),
      features: formatMessage(messages.caseStatuses.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.caseStatuses.description),
      showUpsell: !isEntitlementEnabled(Feature.CaseManagement),
    },
    closingReasons: {
      name: 'closingReasons',
      icon: ucMessageSettings,
      title: formatMessage(messages.closingReasons.title),
      features: formatMessage(messages.closingReasons.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.closingReasons.description),
      showUpsell: !isEntitlementEnabled(Feature.CaseManagement),
    },
    formAppearance: {
      name: 'formAppearance',
      icon: ucMessageSettings,
      title: formatMessage(messages.formAppearance.title),
      features: formatMessage(messages.formAppearance.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.formAppearance.description),
      showUpsell: !isEntitlementEnabled(Feature.CaseManagement),
    },
    addChannelLiveHotline: {
      name: 'addChannelLiveHotline',
      icon: ulPhoneOutgoing02,
      title: formatMessage(messages.addChannelLiveHotline.title),
      features: formatMessage(messages.addChannelLiveHotline.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.addChannelLiveHotline.description),
      showUpsell: !isAllowedMultipleReportSources,
    },
    addChannelAiHotline: {
      name: 'addChannelAiHotline',
      icon: ucAiPhone,
      title: formatMessage(messages.addChannelAiHotline.title),
      features: formatMessage(messages.addChannelAiHotline.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.addChannelAiHotline.description),
      showUpsell: !isAllowedMultipleReportSources,
    },
    addChannelAutomatedHotline: {
      name: 'addChannelAutomatedHotline',
      icon: ulPhoneCall01,
      title: formatMessage(messages.addChannelAutomatedHotline.title),
      features: formatMessage(messages.addChannelAutomatedHotline.features)
        .split(`\n`)
        .filter(Boolean),
      description: formatMessage(messages.addChannelAutomatedHotline.description),
      showUpsell: !isAllowedMultipleReportSources,
    },
    addChannelForm: {
      name: 'addChannelForm',
      icon: ulFile02,
      title: formatMessage(messages.addChannelForm.title),
      features: formatMessage(messages.addChannelForm.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.addChannelForm.description),
      showUpsell: !canAddMoreForms,
    },
    addMember: {
      name: 'addMember',
      icon: ulSettings01,
      title: formatMessage(messages.addMember.title),
      features: formatMessage(messages.addMember.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.addMember.description),
      showUpsell: !canAddMoreUsers,
    },
    automaticDueDate: {
      name: 'automaticDueDate',
      icon: ulSettings01,
      title: formatMessage(messages.automaticDueDate.title),
      features: formatMessage(messages.automaticDueDate.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.automaticDueDate.description),
      showUpsell: !isEntitlementEnabled(Feature.CaseManagement),
    },
    endToEndEncryption: {
      name: 'endToEndEncryption',
      icon: ulSettings01,
      title: formatMessage(messages.endToEndEncryption.title),
      features: formatMessage(messages.endToEndEncryption.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.endToEndEncryption.description),
      showUpsell: !isAllowedE2EE,
    },
    schoolEnableAccessKey: {
      name: 'schoolEnableAccessKey',
      icon: ucMessageSettings,
      title: formatMessage(messages.schoolEnableAccessKey.title),
      features: formatMessage(messages.schoolEnableAccessKey.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.schoolEnableAccessKey.description),
      showUpsell: !isEntitlementEnabled(Feature.CaseManagement),
    },
    materials: material => ({
      name: 'materials',
      icon: ulFolder,
      title: formatMessage(messages.materials.title),
      features: formatMessage(messages.materials.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.materials.description),
      showUpsell: !material.downloadLink || !material.isAccessible,
    }),
    sso: {
      name: 'sso',
      icon: ulSettings01,
      title: formatMessage(messages.sso.title),
      features: formatMessage(messages.sso.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.sso.description),
      showUpsell: !isAllowedSSO,
    },
    webhooks: {
      name: 'webhooks',
      icon: ulZap,
      title: formatMessage(messages.webhooks.title),
      features: formatMessage(messages.webhooks.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.webhooks.description),
      showUpsell: !isAllowedWebhooks,
    },
    apiKeys: {
      name: 'apiKeys',
      icon: ulZap,
      title: formatMessage(messages.apiKeys.title),
      features: formatMessage(messages.apiKeys.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.apiKeys.description),
      showUpsell: !isAllowedApi,
    },
    whitelabel: {
      name: 'whitelabel',
      icon: ulBrush01,
      title: formatMessage(messages.whitelabel.title),
      features: formatMessage(messages.whitelabel.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.whitelabel.description),
      image: showCzechImage ? imageWhitelabelCs : imageWhitelabelEn,
      showUpsell: !isAllowedWhitelabel,
    },
    schoolEditUserPermissions: {
      name: 'schoolEditUserPermissions',
      icon: ulFolder,
      title: formatMessage(messages.schoolEditUserPermissions.title),
      features: formatMessage(messages.schoolEditUserPermissions.features)
        .split(`\n`)
        .filter(Boolean),
      description: formatMessage(messages.schoolEditUserPermissions.description),
      showUpsell:
        institution === Institution.School && !isEntitlementEnabled(Feature.CaseManagement),
    },
    integrations: {
      name: 'integrations',
      icon: ucLinking,
      title: formatMessage(messages.integrations.title),
      features: formatMessage(messages.integrations.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.integrations.description),
      showUpsell: !isEntitlementEnabled(Feature.Integrations),
    },
    surveys: {
      name: 'surveys',
      icon: ucClipboard,
      title: formatMessage(messages.surveys.title),
      features: formatMessage(messages.surveys.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.surveys.description),
      image: showCzechImage ? imageSurveysCs : imageSurveysEn,
      showUpsell: !isEntitlementEnabled(Feature.Surveys),
    },
    investigations: {
      name: 'investigations',
      icon: ulSearchRefraction,
      title: formatMessage(messages.investigations.title),
      features: formatMessage(messages.investigations.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.investigations.description),
      image: showCzechImage ? imageInvestigationsCs : imageInvestigationsEn,
      showUpsell: !isEntitlementEnabled(Feature.Investigations),
    },
    ipAllowlist: {
      name: 'ipAllowlist',
      icon: ulServer05,
      title: formatMessage(messages.ipAllowlist.title),
      features: formatMessage(messages.ipAllowlist.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.ipAllowlist.description),
      showUpsell: !isPartnerAdministration && !isEntitlementEnabled(Feature.IpAllowlist),
    },
    employeeManagement: {
      name: 'employeeManagement',
      icon: ulUsers02,
      title: formatMessage(messages.employeeManagement.title),
      features: formatMessage(messages.employeeManagement.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.employeeManagement.description),
      showUpsell:
        !isEntitlementEnabled(Feature.Integrations) ||
        (!isEntitlementEnabled(Feature.EmployeeManagement) && !hasEmployees),
      image: showCzechImage ? imageEmployeeManagementCs : imageEmployeeManagementEn,
    },
    disclosures: {
      name: 'disclosures',
      icon: ulFileShield02,
      title: formatMessage(messages.disclosures.title),
      features: formatMessage(messages.disclosures.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.disclosures.description),
      showUpsell: !isEntitlementEnabled(Feature.Disclosures),
    },
    customDocuments: {
      name: 'customDocuments',
      icon: ulFolderPlus,
      title: formatMessage(messages.customDocuments.title),
      features: formatMessage(messages.customDocuments.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.customDocuments.description),
      showUpsell: !isEntitlementEnabled(Feature.CustomDocuments),
    },
    caseManagement: {
      name: 'caseManagement',
      icon: ulInbox01,
      title: formatMessage(messages.caseManagement.title),
      features: formatMessage(messages.caseManagement.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.caseManagement.description),
      showUpsell: !isEntitlementEnabled(Feature.CaseManagement),
    },
  }
}
